import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import Logo from "../components/Logo";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

function formatErr(d) {
  if (!d) return "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d?.msg || JSON.stringify(d);
}

export default function OrganizerSetup() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [info, setInfo] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/auth/organizer-link/${token}`)
      .then((res) => setInfo(res.data))
      .catch((err) => setError(formatErr(err.response?.data?.detail) || "Invalid link"));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/auth/organizer-setup", { token, name, password });
      loginWithToken(res.data.access_token, res.data.user);
      toast.success("Account ready. Welcome!");
      navigate("/organizer", { replace: true });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      <header className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center">
          <Link to="/" data-testid="setup-home-link"><Logo /></Link>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md bg-white border border-[#E2E8F0] p-8 md:p-10" data-testid="setup-card">
          {error && (
            <div className="text-center" data-testid="setup-error">
              <p className="kicker mb-3 text-[#EF4444]">LINK INVALID</p>
              <h1 className="font-display text-2xl font-bold text-[#0F172A]">{error}</h1>
              <Link to="/login" className="mt-6 inline-flex items-center text-sm font-semibold text-[#5B4CFF] hover:underline">
                Go to sign in
              </Link>
            </div>
          )}

          {!error && info && (
            <>
              <p className="kicker mb-2">ORGANIZER SETUP</p>
              <h1 className="font-display text-2xl md:text-3xl font-bold text-[#0F172A]">
                Welcome to {info.event_name}
              </h1>
              <p className="mt-2 text-sm text-[#475569]">
                Choose a password to access your event dashboard.
              </p>
              {info.used && (
                <div className="mt-4 border border-[#E2E8F0] p-3 text-xs text-[#475569] flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-[#10B981]" /> This link was already used — setting a new password will replace the existing organizer credentials.
                </div>
              )}
              <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="setup-form">
                <div>
                  <label className="block text-xs font-mono tracking-widest text-[#475569] mb-2">YOUR NAME</label>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-white border border-[#E2E8F0] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#5B4CFF]"
                    data-testid="setup-name-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-mono tracking-widest text-[#475569] mb-2">PASSWORD</label>
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-white border border-[#E2E8F0] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#5B4CFF]"
                    minLength={8}
                    data-testid="setup-password-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-mono tracking-widest text-[#475569] mb-2">CONFIRM PASSWORD</label>
                  <input
                    type="password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="w-full bg-white border border-[#E2E8F0] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#5B4CFF]"
                    minLength={8}
                    data-testid="setup-confirm-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#5B4CFF] hover:bg-[#4a3dde] disabled:opacity-60 text-white font-semibold py-3 transition-colors"
                  data-testid="setup-submit-btn"
                >
                  {loading ? "Creating…" : "Create account"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
